import { useState, useEffect } from 'react'
import {
  fetchElementDomainList,
  registerElementDomain,
  fetchElementDomainDnsRecords,
  verifyElementDomain
} from '../../api'
import type { ElementDomain, ElementDnsRecord } from '../../types'

export function ElementDomainsPage() {
  const [registeredDomainList, setRegisteredDomainList] = useState<ElementDomain[]>([])
  const [newDomainNameInput, setNewDomainNameInput] = useState<string>('')
  const [selectedDomainDnsRecords, setSelectedDomainDnsRecords] = useState<ElementDnsRecord[]>([])
  const [selectedDomainName, setSelectedDomainName] = useState<string>('')
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null)
  const [isDomainListLoading, setIsDomainListLoading] = useState<boolean>(false)
  const [isDomainRegistering, setIsDomainRegistering] = useState<boolean>(false)
  const [isDnsRecordsLoading, setIsDnsRecordsLoading] = useState<boolean>(false)
  const [isDomainVerifying, setIsDomainVerifying] = useState<boolean>(false)
  const [domainVerificationResult, setDomainVerificationResult] = useState<Record<string, boolean> | null>(null)
  const [domainPageError, setDomainPageError] = useState<string | null>(null)

  useEffect(() => {
    loadRegisteredDomains()
  }, [])

  async function loadRegisteredDomains(): Promise<void> {
    setIsDomainListLoading(true)
    try {
      const retrievedDomainList = await fetchElementDomainList()
      setRegisteredDomainList(retrievedDomainList)
    } catch (domainLoadError: unknown) {
      const errorMessage = domainLoadError instanceof Error ? domainLoadError.message : 'Failed to load domains'
      setDomainPageError(errorMessage)
    } finally {
      setIsDomainListLoading(false)
    }
  }

  async function handleDomainRegistrationSubmit(): Promise<void> {
    if (!newDomainNameInput.trim()) return
    setIsDomainRegistering(true)
    setDomainPageError(null)
    try {
      const registrationResult = await registerElementDomain(newDomainNameInput.trim())
      setNewDomainNameInput('')
      await loadRegisteredDomains()
      await handleViewDnsRecords(registrationResult.domainId, registrationResult.domain)
    } catch (registrationError: unknown) {
      const errorMessage = registrationError instanceof Error ? registrationError.message : 'Failed to register domain'
      setDomainPageError(errorMessage)
    } finally {
      setIsDomainRegistering(false)
    }
  }

  async function handleViewDnsRecords(
    domainId: string,
    domainName: string
  ): Promise<void> {
    setIsDnsRecordsLoading(true)
    setSelectedDomainId(domainId)
    setSelectedDomainName(domainName)
    setDomainVerificationResult(null)
    try {
      const dnsRecordsResponse = await fetchElementDomainDnsRecords(domainId)
      setSelectedDomainDnsRecords(dnsRecordsResponse.requiredDnsRecords)
    } catch (dnsRecordsError: unknown) {
      const errorMessage = dnsRecordsError instanceof Error ? dnsRecordsError.message : 'Failed to load DNS records'
      setDomainPageError(errorMessage)
    } finally {
      setIsDnsRecordsLoading(false)
    }
  }

  async function handleDomainVerification(): Promise<void> {
    if (!selectedDomainId) return
    setIsDomainVerifying(true)
    setDomainPageError(null)
    try {
      const verificationResult = await verifyElementDomain(selectedDomainId)
      setDomainVerificationResult(verificationResult.dnsChecks)
      if (verificationResult.domainIsFullyVerified) {
        await loadRegisteredDomains()
      }
    } catch (verificationError: unknown) {
      const errorMessage = verificationError instanceof Error ? verificationError.message : 'Failed to verify domain'
      setDomainPageError(errorMessage)
    } finally {
      setIsDomainVerifying(false)
    }
  }

  function copyDnsRecordToClipboard(recordValue: string): void {
    navigator.clipboard.writeText(recordValue)
  }

  return (
    <div className="element-domains-page">
      <div className="element-domains-header">
        <h2 className="element-domains-title">Domain Management</h2>
        <p className="element-domains-subtitle">
          Add and verify the domains you want to send and receive email from
        </p>
      </div>

      <div className="element-domain-registration-form">
        <input
          type="text"
          className="element-domain-name-input"
          placeholder="yourdomain.com"
          value={newDomainNameInput}
          onChange={changeEvent => setNewDomainNameInput(changeEvent.target.value)}
          onKeyDown={keyboardEvent => {
            if (keyboardEvent.key === 'Enter') handleDomainRegistrationSubmit()
          }}
          disabled={isDomainRegistering}
        />
        <button
          className="element-domain-register-button"
          onClick={handleDomainRegistrationSubmit}
          disabled={isDomainRegistering || !newDomainNameInput.trim()}
        >
          {isDomainRegistering ? 'Adding...' : 'Add Domain'}
        </button>
      </div>

      {domainPageError && (
        <div className="element-domain-error-message" role="alert">
          {domainPageError}
        </div>
      )}

      <div className="element-registered-domain-list">
        {isDomainListLoading ? (
          <div className="element-loading-indicator">Loading domains...</div>
        ) : registeredDomainList.length === 0 ? (
          <div className="element-domains-empty-state">
            <p>No domains registered yet. Add your first domain above.</p>
          </div>
        ) : (
          registeredDomainList.map(registeredDomain => (
            <div
              key={registeredDomain.id}
              className="element-domain-list-item"
            >
              <div className="element-domain-item-info">
                <span className="element-domain-name">{registeredDomain.domain}</span>
                <span className={`element-domain-status-badge ${registeredDomain.verified ? 'element-domain-status-badge--verified' : 'element-domain-status-badge--unverified'}`}>
                  {registeredDomain.verified ? 'Verified' : 'Unverified'}
                </span>
              </div>
              <div className="element-domain-item-actions">
                <button
                  className="element-domain-action-button"
                  onClick={() => handleViewDnsRecords(registeredDomain.id, registeredDomain.domain)}
                >
                  View DNS Records
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedDomainDnsRecords.length > 0 && (
        <div className="element-dns-records-panel">
          <div className="element-dns-records-panel-header">
            <h3 className="element-dns-records-title">
              DNS Records for {selectedDomainName}
            </h3>
            <p className="element-dns-records-instructions">
              Add these records in your Cloudflare DNS dashboard exactly as shown.
              After adding all records click Verify Domain.
            </p>
          </div>

          {isDnsRecordsLoading ? (
            <div className="element-loading-indicator">Loading DNS records...</div>
          ) : (
            <div className="element-dns-records-list">
              {selectedDomainDnsRecords.map((dnsRecord, recordIndex) => (
                <div key={recordIndex} className="element-dns-record-item">
                  <div className="element-dns-record-header">
                    <span className="element-dns-record-type">{dnsRecord.recordType}</span>
                    <span className="element-dns-record-purpose">{dnsRecord.purpose}</span>
                  </div>
                  <div className="element-dns-record-name-row">
                    <span className="element-dns-record-field-label">Name</span>
                    <code className="element-dns-record-value">{dnsRecord.recordName}</code>
                    <button
                      className="element-dns-copy-button"
                      onClick={() => copyDnsRecordToClipboard(dnsRecord.recordName)}
                      aria-label="Copy record name"
                    >
                      Copy
                    </button>
                  </div>
                  <div className="element-dns-record-value-row">
                    <span className="element-dns-record-field-label">Value</span>
                    <code className="element-dns-record-value element-dns-record-value--long">
                      {dnsRecord.recordValue}
                    </code>
                    <button
                      className="element-dns-copy-button"
                      onClick={() => copyDnsRecordToClipboard(dnsRecord.recordValue)}
                      aria-label="Copy record value"
                    >
                      Copy
                    </button>
                  </div>
                  {dnsRecord.priority && (
                    <div className="element-dns-record-priority-row">
                      <span className="element-dns-record-field-label">Priority</span>
                      <code className="element-dns-record-value">{dnsRecord.priority}</code>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="element-dns-verify-section">
            <button
              className="element-dns-verify-button"
              onClick={handleDomainVerification}
              disabled={isDomainVerifying}
            >
              {isDomainVerifying ? 'Verifying...' : 'Verify Domain'}
            </button>

            {domainVerificationResult && (
              <div className="element-dns-verification-results">
                {Object.entries(domainVerificationResult).map(([checkName, checkPassed]) => (
                  <div
                    key={checkName}
                    className={`element-dns-check-row ${checkPassed ? 'element-dns-check-row--passed' : 'element-dns-check-row--failed'}`}
                  >
                    <span className="element-dns-check-indicator">
                      {checkPassed ? '✓' : '✗'}
                    </span>
                    <span className="element-dns-check-name">{checkName}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
